import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { AppShell } from './app/AppShell';
import { AppProviders } from './app/AppProviders';
import './app/styles.css';
import { GuestTransferPage } from './features/style-transfer/pages/GuestTransferPage';
import { MemberTransferPage } from './features/style-transfer/pages/MemberTransferPage';
import { MemberLoginOverlay } from './features/auth/components/MemberLoginOverlay';
import { PricingModal } from './features/payment/components/PricingModal';
import { PaymentResultPage } from './features/payment/pages/PaymentResultPage';
import { GoogleCallbackPage } from './features/auth/pages/GoogleCallbackPage';
import { InviteSummaryDialog } from './features/referrals/components/InviteSummaryDialog';
import { useQuotaSync } from './features/style-transfer/hooks/useQuotaSync';
import {
  useSessionStore,
  selectIsAuthenticated,
  selectQuota,
  selectToken,
  selectQuotaStatus
} from './features/style-transfer/state/sessionStore';
import {
  useAuthStore,
  selectUserEmail,
  selectUserName
} from './features/auth/state/authStore';
import { LegalDocumentPage, resolveLegalDocumentType } from './features/legal/pages/LegalDocumentPage';

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Root element #root not found');
}

function AppContent() {
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  // 支付结果页「重新支付」带 ?pricing=1 回到首页并直接打开购买弹窗
  const [isPricingOpen, setIsPricingOpen] = useState(
    () => new URLSearchParams(window.location.search).get('pricing') === '1'
  );
  const isAuthenticated = useSessionStore(selectIsAuthenticated);
  const quota = useSessionStore(selectQuota);
  const token = useSessionStore(selectToken);
  const quotaStatus = useSessionStore(selectQuotaStatus);
  const userEmail = useAuthStore(selectUserEmail);
  const userName = useAuthStore(selectUserName);
  const signOut = useAuthStore((state) => state.signOut);
  const previousAuthRef = useRef(isAuthenticated);

  useQuotaSync();

  useEffect(() => {
    if (previousAuthRef.current !== isAuthenticated) {
      previousAuthRef.current = isAuthenticated;
      if (isLoginOpen && isAuthenticated) {
        setIsLoginOpen(false);
      }
      if (!isAuthenticated && isInviteOpen) {
        setIsInviteOpen(false);
      }
    }
  }, [isAuthenticated, isInviteOpen, isLoginOpen]);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.has('pricing')) {
      url.searchParams.delete('pricing');
      window.history.replaceState(null, '', url.pathname + url.search + url.hash);
    }
  }, []);

  const handleOpenLogin = useCallback(() => {
    setIsLoginOpen(true);
  }, []);

  const handleCloseLogin = useCallback(() => {
    setIsLoginOpen(false);
  }, []);

  const handleOpenInvite = useCallback(() => {
    setIsInviteOpen(true);
  }, []);

  const handleCloseInvite = useCallback(() => {
    setIsInviteOpen(false);
  }, []);

  const handleOpenPricing = useCallback(() => {
    setIsPricingOpen(true);
  }, []);

  const handleClosePricing = useCallback(() => {
    setIsPricingOpen(false);
  }, []);

  const effectiveQuota = quota ?? token?.quota ?? null;
  const readyQuota = quotaStatus === 'ready' ? effectiveQuota : null;
  const profile = isAuthenticated
    ? {
      email: userEmail,
      userName,
      quotaRemaining: readyQuota?.remaining ?? null,
      quotaTotal: readyQuota?.total ?? null,
      quotaStatus
    }
    : undefined;
  const guestQuotaStatus = !isAuthenticated ? quotaStatus : 'idle';
  const guestQuotaRemaining =
    !isAuthenticated && readyQuota
      ? readyQuota.remaining ?? null
      : null;

  return (
    <>
      <AppShell
        onLoginClick={!isAuthenticated ? handleOpenLogin : undefined}
        onVoucherRedeemClick={isAuthenticated ? handleOpenLogin : undefined}
        onAddFundsClick={isAuthenticated ? handleOpenPricing : undefined}
        onInviteClick={isAuthenticated ? handleOpenInvite : undefined}
        isLoginActive={!isAuthenticated && isLoginOpen}
        isAuthenticated={isAuthenticated}
        profile={profile}
        onSignOut={signOut}
        guestQuotaRemaining={guestQuotaRemaining}
        guestQuotaStatus={guestQuotaStatus}
      >
        {isAuthenticated ? <MemberTransferPage /> : <GuestTransferPage />}
      </AppShell>
      {isLoginOpen ? <MemberLoginOverlay onClose={handleCloseLogin} /> : null}
      {isInviteOpen && isAuthenticated ? (
        <InviteSummaryDialog onClose={handleCloseInvite} />
      ) : null}
      {isPricingOpen && isAuthenticated ? <PricingModal onClose={handleClosePricing} /> : null}
    </>
  );
}

function RootApp() {
  const legalDocumentType =
    typeof window !== 'undefined' ? resolveLegalDocumentType(window.location.pathname) : null;
  const isOAuthCallback =
    typeof window !== 'undefined' && window.location.pathname === '/auth/google/callback';
  const isPaymentResult =
    typeof window !== 'undefined' && window.location.pathname === '/payment/result';

  return (
    <AppProviders>
      {legalDocumentType ? (
        <LegalDocumentPage documentType={legalDocumentType} />
      ) : isOAuthCallback ? (
        <GoogleCallbackPage />
      ) : isPaymentResult ? (
        <PaymentResultPage />
      ) : (
        <AppContent />
      )}
    </AppProviders>
  );
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <RootApp />
  </React.StrictMode>
);
